import { useAppData } from "@/context/AppDataContext";
import {
  DEALER_DIRECT_QUEUE_DEPARTMENTS,
  hasAnyOfAgentIds,
  hasAnyOfDepartments,
  UNDERWRITING_AGENTS,
  UNDERWRITING_DEPARTMENTS,
} from "@/constants/appRoles";
import { isOfflineMode } from "@/helpers/offlineMode";
import { Navigate } from "react-router-dom";
import { useEffect } from "react";
import type { Worker } from "@/types/appData";

interface RequireUnderwritingAccessProps {
  children: React.ReactNode;
}

function logCurrentUserDepartment(area: string, currentUser: Worker | null) {
  console.info(`[${area} access] Current user department`, {
    userId: currentUser?.userId,
    name: currentUser?.name,
    email: currentUser?.email,
    departmentId: currentUser?.departmentId,
    department: currentUser?.department,
  });
}

export function RequireUnderwritingQueueAccess({ children }: RequireUnderwritingAccessProps) {
  const { currentUser, isLoading } = useAppData();

  useEffect(() => {
    if (!currentUser) return;

    logCurrentUserDepartment("Underwriting Queue", currentUser);
  }, [currentUser]);

  // Avoid redirecting while currentUser's department is still being resolved
  // (e.g. on page refresh), since currentUser starts null until the app data
  // finishes loading, which would otherwise incorrectly look like "no access".
  if (isLoading) {
    return null;
  }

  const hasAccess =
    hasAnyOfDepartments(currentUser?.departmentId, UNDERWRITING_DEPARTMENTS) ||
    hasAnyOfAgentIds(currentUser?.userId, UNDERWRITING_AGENTS);
  if (!isOfflineMode() && !hasAccess) {
    return <Navigate to="/pipeline" replace />;
  }
  return <>{children}</>;
}

export function RequireDealerDirectQueueAccess({ children }: RequireUnderwritingAccessProps) {
  const { currentUser, isLoading } = useAppData();

  useEffect(() => {
    if (!currentUser) return;

    logCurrentUserDepartment("Dealer Direct", currentUser);
  }, [currentUser]);

  // Avoid redirecting while currentUser's department is still being resolved
  // (e.g. on page refresh), since currentUser starts null until the app data
  // finishes loading, which would otherwise incorrectly look like "no access".
  if (isLoading) {
    return null;
  }

  const hasAccess = hasAnyOfDepartments(currentUser?.departmentId, DEALER_DIRECT_QUEUE_DEPARTMENTS);

  if (!isOfflineMode() && !hasAccess) {
    return <Navigate to="/pipeline" replace />;
  }
  return <>{children}</>;
}
